import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fetchBillingOverview, cancelSubscriptionAtPeriodEnd } from '../services/billingApi';
import DashboardNavbar from '../dashboardNavbar';
import { Card, Btn, Tag, TagDot, Field, Input, Select, FormGrid } from '../ui/primitives';
import { useAppContext } from '../../appprovider';
import { canAccessBilling } from '../services/instructorPortalAccess';
import PackageDialog from '../course/packagedialog';
import BillingPlanOverview from './BillingPlanOverview';
import BillingInAppPurchaseSetting from './BillingInAppPurchaseSetting';
import BillingIncome from './BillingIncome';
import PayoutSetup from './PayoutSetup';
import { ADMIN_TABS, SYSTEM_TABS } from './adminSectionTabs';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

export { ADMIN_TABS, SYSTEM_TABS };
export { default as AdminUsers } from './AdminUsers';
export { default as AdminCourseCatalog } from './AdminCourseCatalog';
export { default as SystemApplicationUsers } from './SystemApplicationUsers';

/* ============================================================
   Admin / Billing / System scaffolding pages.

   These are UI placeholders for features whose APIs are not wired
   yet (white-labeling, enterprise course catalog, user invite/import,
   LLM/SSO/LMS integrations, location & standards, billing). The
   layout, tabs and action options match the product design so the
   backend can be plugged in later without UI rework.
   ============================================================ */

const BILLING_TABS = [
  { title: 'Package & Plan', link: '/Portal/Instructor/billing' },
  { title: 'Invoices', link: '/Portal/Instructor/billing/invoices' },
  { title: 'Income', link: '/Portal/Instructor/billing/income' },
];

/* ---- layout primitives ---- */
const Shell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) { padding: 0 14px 28px; }
`;

const Hero = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.space} auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  h2 { margin: 0; color: ${({ theme }) => theme.text}; font-family: ${({ theme }) => theme.fontHead}; font-size: 16px; font-weight: 700; letter-spacing: -.01em; }
  p { margin: 2px 0 0; color: ${({ theme }) => theme.textMuted}; font-size: ${({ theme }) => theme.fsSm}; }
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Wrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.gap};
`;

const CardTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 750;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardText = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const AdminField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 12px;
  label { font-size: ${({ theme }) => theme.fsSm}; font-weight: 650; color: ${({ theme }) => theme.text}; }
  input, select {
    height: ${({ theme }) => theme.controlH};
    padding: 0 12px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.rMd};
    background: ${({ theme }) => theme.surface2};
    color: ${({ theme }) => theme.text};
    font-size: ${({ theme }) => theme.fsSm};
    outline: none;
  }
`;

const Note = styled.div`
  margin-top: 6px;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  font-style: italic;
`;

const StatusNote = styled.div`
  margin-top: 8px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.45;
`;

const ErrorNote = styled(StatusNote)`
  color: ${({ theme }) => theme.danger};
`;

const SuccessNote = styled(StatusNote)`
  color: ${({ theme }) => theme.success || theme.accent};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fsSm};
  th, td { text-align: left; padding: 10px 12px; border-bottom: 1px solid ${({ theme }) => theme.border}; }
  th { color: ${({ theme }) => theme.textSubtle}; font-weight: 700; font-size: ${({ theme }) => theme.fsMicro}; text-transform: uppercase; letter-spacing: .05em; }
`;

const PlanHint = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.45;
`;

const ModalFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

const FileInput = styled.input`
  width: 100%;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
`;

/* A page-level shell that draws the section tab bar + hero + content. */
function SectionPage({ tabs, title, subtitle, actions, children }) {
  return (
    <Shell>
      <DashboardNavbar menuItems={tabs} />
      <Hero>
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <HeroActions>{actions}</HeroActions>}
      </Hero>
      <Wrap>{children}</Wrap>
    </Shell>
  );
}

const ApiNote = () => (
  <Note>This feature's API is not connected yet — the option is in place and will activate once the endpoint is integrated.</Note>
);

/* ============================================================
   ADMIN · Users — create / invite / import
   ============================================================ */
/* ============================================================
   ADMIN · Integrations — LLM / SSO / LMS, etc.
   ============================================================ */
export function AdminIntegrations() {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const integrations = [
    { name: 'LLM provider', desc: 'Connect the AI model used for question and content generation (OpenAI, Anthropic, Azure…).' },
    { name: 'SSO', desc: 'Single sign-on via SAML / OIDC (Okta, Azure AD, Google Workspace).' },
    { name: 'LMS', desc: 'Sync courses, rosters and grades with your LMS (Canvas, Moodle, Blackboard).' },
    { name: 'License', desc: 'Manage product license keys and seat allocation.' },
    { name: 'Question Type', desc: 'Enable or restrict the question formats available to instructors.' },
  ];
  return (
    <div>
      <SectionPage
        tabs={ADMIN_TABS}
        title="Integrations"
        subtitle="Configure the platforms and services your organization connects to."
      >
        <Grid>
          {integrations.map((it) => (
            <Card $pad key={it.name}>
              <CardTitle>{it.name} <Tag $tone="gray"><TagDot />Not connected</Tag></CardTitle>
              <CardText>{it.desc}</CardText>
              <Btn onClick={() => guardAction('action.admin.integration.configure')}>Configure</Btn>
              <ApiNote />
            </Card>
          ))}
        </Grid>
      </SectionPage>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </div>
  );
}

/* ============================================================
   ADMIN · Location & Standard
   ============================================================ */
export function AdminLocationStandard() {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  return (
    <div>
    <SectionPage
      tabs={ADMIN_TABS}
      title="Location & Standard"
      subtitle="Define the locations and learning standards (e.g. regional, accreditation or compliance frameworks) used across your courses."
      actions={<Btn $variant="primary" onClick={() => guardAction('action.admin.location.add')}>Add location / standard</Btn>}
    >
      <Grid>
        <Card $pad>
          <CardTitle>Locations</CardTitle>
          <CardText>Regions / sites that organize your courses and enrollees.</CardText>
          <AdminField><label>Location name</label><input placeholder="e.g. North America" /></AdminField>
          <Btn onClick={() => guardAction('action.admin.location.add')}>Add location</Btn>
          <ApiNote />
        </Card>
        <Card $pad>
          <CardTitle>Standards</CardTitle>
          <CardText>Learning or compliance standards mapped to course content.</CardText>
          <AdminField><label>Standard name</label><input placeholder="e.g. US Common Core" /></AdminField>
          <Btn onClick={() => guardAction('action.admin.standard.add')}>Add standard</Btn>
          <ApiNote />
        </Card>
      </Grid>
    </SectionPage>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </div>
  );
}

/* ============================================================
   BILLING — package & plan (moved out of Settings)
   ============================================================ */
export function Billing() {
  const { entitlements } = useAppContext();
  const { lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [showPlans, setShowPlans] = useState(false);
  const userId = localStorage.getItem('userId');

  return (
    <div>
    <SectionPage
      tabs={BILLING_TABS}
      title="Billing"
      subtitle="Manage your package, plan capacity, and in-app purchase settings."
      actions={<Btn $variant="primary" onClick={() => setShowPlans(true)}>Upgrade plan</Btn>}
    >
      <Card $pad style={{ marginBottom: 16 }}>
        <BillingPlanOverview entitlements={entitlements} />
      </Card>

      <PayoutSetup />

      <BillingInAppPurchaseSetting userId={userId} onUpgradePlan={() => setShowPlans(true)} />

      <PackageDialog open={showPlans} handleClose={() => setShowPlans(false)} />
    </SectionPage>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </div>
  );
}

/* ============================================================
   ADMIN · Plan & billing — read-only plan view inside the Admin
   section. Org admins see the org's plan, capacity, and included
   features here; actual billing management (upgrade, Stripe,
   invoices) stays on the owner-only Billing page.
   ============================================================ */
export function AdminPlanBilling() {
  const { entitlements } = useAppContext();
  const navigate = useNavigate();
  const canManage = canAccessBilling(entitlements);

  return (
    <SectionPage
      tabs={ADMIN_TABS}
      title="Plan & billing"
      subtitle={canManage
        ? 'Your organisation’s plan and capacity.'
        : 'Your organisation’s plan and capacity. Plan changes are managed by the organisation owner.'}
      actions={canManage
        ? (
          <Btn $variant="primary" onClick={() => navigate('/Portal/Instructor/billing')}>
            Manage billing
          </Btn>
        )
        : null}
    >
      <Card $pad style={{ marginBottom: 16 }}>
        <BillingPlanOverview entitlements={entitlements} />
      </Card>
    </SectionPage>
  );
}

/* ============================================================
   BILLING · Income — payout account + course-sale earnings
   ============================================================ */
export function BillingIncomePage() {
  return (
    <SectionPage
      tabs={BILLING_TABS}
      title="Income"
      subtitle="Track your course-sale earnings and payouts to your bank."
    >
      <BillingIncome />
    </SectionPage>
  );
}

const REASON_LABEL = {
  subscription_create: 'New subscription',
  subscription_cycle: 'Renewal',
  subscription_update: 'Plan change',
  manual: 'Manual',
};

function fmtDate(epochSeconds) {
  if (!epochSeconds) return '—';
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function fmtMoney(cents, currency) {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch (_) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function titleCase(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

const StatusPill = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  text-transform: capitalize;
  background: ${({ $ok, theme }) => ($ok ? 'rgba(34,178,110,.12)' : 'rgba(230,140,20,.14)')};
  color: ${({ $ok }) => ($ok ? 'oklch(0.55 0.14 158)' : 'oklch(0.55 0.13 66)')};
`;

export function BillingInvoices() {
  const userId = localStorage.getItem('userId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      setData(await fetchBillingOverview(userId));
    } catch (e) {
      setError('Could not load your billing history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const doCancel = async () => {
    setCanceling(true); setError(null);
    try {
      setData(await cancelSubscriptionAtPeriodEnd(userId));
      setConfirmOpen(false);
    } catch (e) {
      setError(e.message || 'Could not cancel the subscription.');
    } finally {
      setCanceling(false);
    }
  };

  const invoices = data?.invoices || [];
  const hasPaidPlan = data?.hasBilling && data?.subscriptionId;
  const scheduledToCancel = data?.cancelAtPeriodEnd;

  return (
    <SectionPage tabs={BILLING_TABS} title="Invoices" subtitle="Your purchase history, upcoming charge, and receipts.">
      {error && (
        <Card $pad style={{ borderColor: 'rgba(220,60,60,.35)', marginBottom: 14 }}>
          <CardText style={{ color: 'oklch(0.55 0.16 25)', margin: 0 }}>{error}</CardText>
        </Card>
      )}

      {/* Current plan + next charge + cancel */}
      <Card $pad style={{ marginBottom: 14 }}>
        {loading ? (
          <CardText style={{ margin: 0, opacity: 0.6 }}>Loading your billing details…</CardText>
        ) : !data?.hasBilling ? (
          <>
            <CardTitle>No billing history yet</CardTitle>
            <CardText style={{ margin: 0 }}>Once you purchase a paid plan, your subscription and receipts will appear here.</CardText>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
              <div>
                <Tag $tone="accent" style={{ marginBottom: 6 }}>Current plan</Tag>
                <CardTitle style={{ margin: '2px 0' }}>{titleCase(data.tier)}</CardTitle>
                <CardText style={{ margin: 0 }}>
                  {scheduledToCancel ? (
                    <>Scheduled to cancel — you keep <strong>{titleCase(data.tier)}</strong> until <strong>{fmtDate(data.currentPeriodEnd)}</strong>, then drop to Starter.</>
                  ) : hasPaidPlan ? (
                    <>Next charge: <strong>{fmtMoney(data.nextChargeAmountCents, data.currency)}</strong>
                      {data.interval ? ` / ${data.interval}` : ''} on <strong>{fmtDate(data.currentPeriodEnd)}</strong>.</>
                  ) : (
                    <>You're on the free Starter plan — no upcoming charge.</>
                  )}
                </CardText>
              </div>
              {hasPaidPlan && !scheduledToCancel && (
                <Btn $variant="danger" onClick={() => setConfirmOpen(true)}>Cancel subscription</Btn>
              )}
            </div>

            {confirmOpen && (
              <div style={{ marginTop: 14, padding: 14, borderRadius: 12, border: '1px solid rgba(220,60,60,.3)', background: 'rgba(220,60,60,.05)' }}>
                <CardText style={{ margin: '0 0 10px' }}>
                  Cancel your <strong>{titleCase(data.tier)}</strong> plan? You'll keep access until <strong>{fmtDate(data.currentPeriodEnd)}</strong> (the end of the period you've already paid for), then move to Starter. No refund is issued.
                </CardText>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn $variant="danger" disabled={canceling} onClick={doCancel}>
                    {canceling ? 'Canceling…' : 'Yes, cancel at period end'}
                  </Btn>
                  <Btn $variant="ghost" disabled={canceling} onClick={() => setConfirmOpen(false)}>Keep my plan</Btn>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Invoice history */}
      <Card $pad>
        <CardTitle>Purchase history</CardTitle>
        <Table>
          <thead>
            <tr><th>Date</th><th>Description</th><th>Amount</th><th>Status</th><th>Receipt</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px 0', opacity: 0.6 }}>Loading…</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px 0', opacity: 0.6 }}>No invoices yet.</td></tr>
            ) : invoices.map((inv) => (
              <tr key={inv.id}>
                <td>{fmtDate(inv.created)}</td>
                <td>{REASON_LABEL[inv.billingReason] || 'Payment'}{inv.number ? ` · ${inv.number}` : ''}</td>
                <td>{fmtMoney(inv.amountCents, inv.currency)}</td>
                <td><StatusPill $ok={inv.status === 'paid'}>{inv.status}</StatusPill></td>
                <td>
                  {inv.hostedInvoiceUrl && (
                    <a href={inv.hostedInvoiceUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 600, marginRight: 12 }}>View</a>
                  )}
                  {inv.invoicePdf && (
                    <a href={inv.invoicePdf} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>PDF</a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </SectionPage>
  );
}

/* ============================================================
   SYSTEM · Course Catalog — StudyAK super user copies course to
   the Enterprise Course Catalog and sets availability.
   ============================================================ */
export function SystemCourseCatalog() {
  const [audience, setAudience] = useState('all');
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const guardCopy = () => guardAction('action.system.catalog.copy');
  return (
    <div>
    <SectionPage
      tabs={SYSTEM_TABS}
      title="Import catalog"
      subtitle="StudyAK super users can copy existing courses into the Enterprise Course Catalog and choose who they're available to."
      actions={<Btn $variant="primary" onClick={() => guardCopy()}>Copy course to Enterprise Catalog</Btn>}
    >
      <Card $pad>
        <CardTitle>Add to Enterprise Catalog</CardTitle>
        <CardText>Pick any course and publish it to the shared catalog.</CardText>
        <AdminField>
          <label>Course</label>
          <select><option value="">Select a course…</option></select>
        </AdminField>
        <AdminField>
          <label>Make available to</label>
          <select value={audience} onChange={(e) => setAudience(e.target.value)}>
            <option value="enterprise">Specific enterprise</option>
            <option value="all">All customers</option>
          </select>
        </AdminField>
        <Btn $variant="primary" onClick={() => guardCopy()}>Publish to catalog</Btn>
        <ApiNote />
      </Card>

      <Card $pad>
        <CardTitle>Published catalog</CardTitle>
        <Table>
          <thead><tr><th>Course</th><th>Availability</th><th>Published</th><th /></tr></thead>
          <tbody>
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: '20px 0', opacity: 0.6 }}>Published courses will appear here once the API is connected.</td></tr>
          </tbody>
        </Table>
      </Card>
    </SectionPage>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </div>
  );
}
